import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Dark-tint the keyless OSM tiles to match the MyRedPark theme (once, globally).
if (typeof document !== "undefined" && !document.getElementById("mrp-map-dark-style")) {
  const st = document.createElement("style");
  st.id = "mrp-map-dark-style";
  st.textContent = ".mrp-map-dark{filter:grayscale(1) invert(0.92) brightness(0.85) contrast(0.95);}.leaflet-container{background:#0A0D10;}";
  document.head.appendChild(st);
}

// Interactive map with a draggable pin — "confirm the exact property location".
// Dark CartoDB tiles match the MyRedPark theme; no API keys in the browser.
export default function MapPinPicker({ lat, lng, onPick, height = 260, testId = "property-map" }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  useEffect(() => {
    if (!mapRef.current || leafletRef.current) return undefined;
    const startLat = lat || 43.65;
    const startLng = lng || -79.38;
    const map = L.map(mapRef.current, { center: [startLat, startLng], zoom: lat ? 17 : 12, zoomControl: true, attributionControl: true });
    // Keyless OSM tiles with a cinematic dark filter (CartoDB now watermarks
    // keyless basemap requests — OSM + filter keeps the dark theme legally free).
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19, className: "mrp-map-dark",
    }).addTo(map);
    const icon = L.divIcon({
      className: "",
      html: '<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#E50914;border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,.55)"></div>',
      iconSize: [22, 22],
      iconAnchor: [11, 20],
    });
    const marker = L.marker([startLat, startLng], { draggable: true, icon }).addTo(map);
    marker.on("dragend", () => { const p = marker.getLatLng(); onPickRef.current(p.lat, p.lng); });
    map.on("click", (e) => { marker.setLatLng(e.latlng); onPickRef.current(e.latlng.lat, e.latlng.lng); });
    leafletRef.current = map;
    markerRef.current = marker;
    return () => { map.remove(); leafletRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (leafletRef.current && markerRef.current && lat && lng) {
      // Smooth, non-bouncing transition to the new selection (§6) — animated
      // setView, no flyTo bounce, no marker re-creation.
      leafletRef.current.setView([lat, lng], Math.max(leafletRef.current.getZoom(), 17), { animate: true, duration: 0.4 });
      markerRef.current.setLatLng([lat, lng]);
    }
  }, [lat, lng]);

  return <div ref={mapRef} data-testid={testId} style={{ height }} className="w-full rounded-xl overflow-hidden border border-[#27272A] relative z-0" />;
}
